import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { Vehicle } from '../types/vehicle';
import { vehicles as fallbackVehicles } from '../data/vehicles';
import { fetchVehiclesFromSheet } from '../utils/sheetVehicles';
type Source = 'sheets' | 'script' | 'local';
interface VehiclesValue {
  vehicles: Vehicle[];
  loading: boolean;
  /** De dónde salieron las unidades que se están mostrando. */
  source: Source;
  /** Motivo por el que no se pudo leer la planilla, para diagnóstico. */
  error: string | null;
  marcas: string[];
  modelos: string[];
  tipos: string[];
  combustibles: string[];
}
const VehiclesContext = createContext<VehiclesValue | null>(null);
const localDisponibles = fallbackVehicles.filter(v => v.disponible);
export function VehiclesProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [vehicles, setVehicles] = useState<Vehicle[]>(localDisponibles);
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState<Source>('local');
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let active = true;
    fetchVehiclesFromSheet().then(result => {
      if (!active) return;
      setVehicles(result.vehicles);
      setSource(result.source);
      setError(null);
    }).catch((err: unknown) => {
      const message = err instanceof Error ? err.message : String(err);
      console.warn('[VCARS] No se pudo leer el catálogo desde Google Sheets:', message);
      if (active) {
        setSource('local');
        setError(message);
      }
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => {
      active = false;
    };
  }, []);
  const value = useMemo<VehiclesValue>(() => {
    const unique = (get: (v: Vehicle) => string) => Array.from(new Set(vehicles.map(get).filter(Boolean))).sort();
    return {
      vehicles,
      loading,
      source,
      error,
      marcas: unique(v => v.marca),
      modelos: unique(v => v.modelo),
      tipos: unique(v => v.tipo),
      combustibles: unique(v => v.combustible)
    };
  }, [vehicles, loading, source, error]);
  return <VehiclesContext.Provider value={value} data-id="element-639" data-mp="3t3kigc91aggo">{children}</VehiclesContext.Provider>;
}
export function useVehicles(): VehiclesValue {
  const ctx = useContext(VehiclesContext);
  if (!ctx) throw new Error('useVehicles debe usarse dentro de VehiclesProvider');
  return ctx;
}